/**
 * Stat tile — la forma correcta cuando el dato es UN número (no un gráfico).
 *
 * Contrato: label (sentence case) · value (proporcional y compacto) · sub (el
 * denominador, para que el % no flote sin base) · meter opcional · chip de
 * procedencia cuando el número no es una medición real.
 *
 * Reglas que se respetan acá:
 * - Figuras PROPORCIONALES en el valor: `tabular-nums` en un número grande hace que
 *   "121" se vea suelto (equal-width digits). Tabular sólo en columnas de tablas.
 * - El color nunca es el único canal: cada tile lleva ícono + label.
 * - El track del meter es un paso más claro del MISMO hue que el fill (blue-on-blue),
 *   así el estado se lee a lo largo de toda la barra.
 */

import type { ReactNode } from "react";

export type TonoTile = "neutro" | "entrega" | "lectura" | "respuesta" | "baja" | "resultado";

const TONOS: Record<TonoTile, { valor: string; icono: string; fill: string; track: string }> = {
  // contraste vs superficie validado ≥ 3:1 en los seis (script del skill dataviz)
  neutro:    { valor: "text-slate-900",   icono: "text-slate-400 bg-slate-50",     fill: "bg-slate-400",   track: "bg-slate-100" },
  entrega:   { valor: "text-slate-900",   icono: "text-slate-500 bg-slate-50",     fill: "bg-slate-600",   track: "bg-slate-100" },
  lectura:   { valor: "text-sky-700",     icono: "text-sky-600 bg-sky-50",         fill: "bg-sky-600",     track: "bg-sky-100" },
  respuesta: { valor: "text-violet-700",  icono: "text-violet-600 bg-violet-50",   fill: "bg-violet-600",  track: "bg-violet-100" },
  baja:      { valor: "text-red-700",     icono: "text-red-600 bg-red-50",         fill: "bg-red-600",     track: "bg-red-100" },
  resultado: { valor: "text-emerald-700", icono: "text-emerald-600 bg-emerald-50", fill: "bg-emerald-600", track: "bg-emerald-100" },
};

interface Props {
  icon: ReactNode;
  label: string;
  /** ya formateado (compacto). Si es null → el dato no existe y se dice, no se inventa un 0 */
  value: string | null;
  /** el denominador o contexto: "de 183 enviados" */
  sub?: string;
  tono?: TonoTile;
  /** 0–100. Sólo cuando la proporción significa algo */
  meterPct?: number;
  /** chip corto: "estimado" · "sin medir" · "simulado" */
  chip?: { texto: string; cls: string };
  /** estado vacío honesto: qué decir cuando value === null */
  vacio?: string;
}

export default function StatTile({ icon, label, value, sub, tono = "neutro", meterPct, chip, vacio }: Props) {
  const t = TONOS[tono];
  const sinDato = value === null;

  return (
    <div className="relative rounded-2xl border border-slate-100 bg-white p-4 shadow-sm hover:border-slate-200 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${t.icono}`}>{icon}</div>
        {chip && (
          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${chip.cls}`}>{chip.texto}</span>
        )}
      </div>

      {/* valor: proporcional (no tabular) y compacto */}
      <p className={`mt-3 text-[30px] leading-none font-semibold tracking-tight ${sinDato ? "text-slate-300" : t.valor}`}>
        {sinDato ? "—" : value}
      </p>
      <p className="mt-1.5 text-[13px] font-medium text-slate-600">{label}</p>
      <p className="text-[11px] text-slate-400 mt-0.5 min-h-[14px]">{sinDato ? vacio || "sin datos" : sub || ""}</p>

      {/* meter: fill sobre un track del mismo hue, un paso más claro */}
      {!sinDato && typeof meterPct === "number" && (
        <div className={`mt-3 h-1.5 rounded-full overflow-hidden ${t.track}`}>
          <div className={`h-full rounded-full ${t.fill} transition-all duration-700`} style={{ width: `${Math.max(1.5, Math.min(100, meterPct))}%` }} />
        </div>
      )}
    </div>
  );
}
